/**
 * IP Services — Client IP detection for server-to-server tracking (CAPI)
 *
 * Strategy: Dual-stack endpoints only. All configured services support both IPv4 and IPv6.
 * Modern browsers implement "happy eyeballs" (RFC 8305), which tries IPv6 first with a
 * ~250ms head start when connecting to dual-stack hosts. If the client has working IPv6,
 * the browser connects via IPv6 and the service returns the IPv6 address. If IPv6 is
 * unavailable or slow, IPv4 wins — which is also the IP that ad platform browser pixels
 * (e.g., Facebook fbq) see from this user.
 *
 * Why not test IPv6-only endpoints?
 * - IPv6-only hostnames (e.g., api6.ipify.org) have only AAAA DNS records
 * - On IPv4-only clients, DNS resolution fails with ERR_NAME_NOT_RESOLVED
 * - This error is logged by the browser's network stack BEFORE any JavaScript
 *   error handler runs — it cannot be caught, suppressed, or silenced
 * - Dual-stack endpoints achieve the same IPv6 detection via happy eyeballs,
 *   without any console errors
 * - If a dual-stack endpoint returns IPv4, the browser prefers IPv4 for all
 *   external connections — Facebook's pixel also sees IPv4 from this user,
 *   so sending IPv4 in CAPI is correct for event matching
 *
 * Speed: All configured services are raced concurrently via Promise.any().
 * The fastest successful response wins. More runners = faster winner + higher
 * reliability if individual services are blocked or decommissioned.
 *
 * Caching: Results are stored in sessionStorage (via pmw.storeData) and persist
 * across page navigations within the same browser tab. IP detection only runs
 * once per tab session.
 */

(function (pmw, $, undefined) {

	const {isRange, isIP, isV4, isV6, version: getIpVersion} = require("range_check")

	// Promise-based IP detection to prevent race conditions
	// Stores the ongoing detection promise so multiple callers can await the same result
	let ipDetectionPromise = null
	 let ipDetectionResolved = false

	const ipServices = {
		"cloudflare": {
			"any": "https://www.cloudflare.com/cdn-cgi/trace",
		},
		// icanhazip seems to regularly be added to malware databases, despite being a legitimate service run by Cloudflare
		// https://blog.apnic.net/2021/06/17/how-a-small-free-ip-tool-survived/
		// https://major.io/p/a-new-future-for-icanhazip/
		// https://major.io/p/extra-icanhaz-services-going-offline/
		"icanhazip"   : {
			"4"  : "https://ipv4.icanhazip.com",
			"6"  : "https://ipv6.icanhazip.com",
			"any": "https://icanhazip.com",
		},
		"ipinfo"      : {
			"any": "https://ipinfo.io/ip",
		},
		"myexternalip": {
			"any": "https://myexternalip.com/raw",
			// "6": "https://api-ipv6.myexternalip.com/raw", // This can return an IPv4 address, so we can't use it
		},
		"ipify"       : {
			"4"  : "https://api4.ipify.org",
			"6"  : "https://api6.ipify.org",
			"any": "https://api64.ipify.org", // If IPv6 is available, AND the client is configured to prefer IPv6, this will return an IPv6 address
		},
		// ident.me - Reliable service with dedicated IPv4 and IPv6 endpoints
		// https://api.ident.me/
		"identme"     : {
			"4"  : "https://4.ident.me/",
			"6"  : "https://6.ident.me/",
			"any": "https://ident.me/",
		},
		// Can't use amazon because its CORS policy blocks requests from the browser
		// "amazon"    : {
		// 	"any": "https://checkip.amazonaws.com/",
		// },
		// Can't use ip.sb because its CORS policy blocks requests from the browser
		// "ip.sb"     : {
		// 	"any": "https://api.ip.sb/ip",
		// },
		// Can't use seeip because its CORS policy blocks requests from the browser
		// "seeip"       : {
		// 	"any": "https://api.seeip.org",
		// 	"4": "https://ipv4.seeip.org",
		// 	"6": "https://ipv6.seeip.org",
		// },
	}

	/**
	 * Default timeout for IP service requests in milliseconds.
	 * If a service doesn't respond within this time, the request is aborted.
	 */
	const IP_SERVICE_TIMEOUT_MS = 3000

	/**
	 * Fetch with timeout using AbortController.
	 * Aborts the request if it takes longer than the specified timeout.
	 *
	 * @param {string} url - The URL to fetch
	 * @param {number} timeoutMs - Timeout in milliseconds (default: IP_SERVICE_TIMEOUT_MS)
	 * @returns {Promise<Response>} The fetch response
	 * @throws {Error} Throws 'AbortError' if the request times out
	 */
	const fetchWithTimeout = (url, timeoutMs = IP_SERVICE_TIMEOUT_MS) => {
		const controller = new AbortController()
		const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

		return fetch(url, { signal: controller.signal })
			.finally(() => clearTimeout(timeoutId))
	}

	pmw.getGeoInfo = {

		cloudflare: async () => {

			const response = await fetchWithTimeout("https://www.cloudflare.com/cdn-cgi/trace")

			if (!response.ok) throw new Error(`The response from Cloudflare was not OK. Status: ${response.status}`)

			let data = await response.text()

			if (!data) throw new Error("No data was returned from Cloudflare")

			// Convert key-value pairs to JSON
			// https://stackoverflow.com/a/39284735/452587
			data = data
				.trim()
				.split("\n")
				.reduce((obj, pair) => {
					pair = pair.split("=")
					return obj[pair[0]] = pair[1], obj
				}, {})

			if (!data.ip || !data.loc) throw new Error("The JSON data is missing the ip or loc key from Cloudflare")

			/**
			 * Cloudflare doesn't return the region.
			 * If the website manager has set a region such as California in the restricted_regions array,
			 * we have to validate the region. This is not possible with the data from Cloudflare.
			 * So we have to abort the process and give the other services a chance to return the region.
			 */
			if (processRegion(data.loc)) throw new Error("We have to validate the region, and this service is not returning the region.")

			return {
				ip         : data.ip,
				countryCode: data.loc,
				service    : "cloudflare",
			}
		},

		geojs: async () => {

			const response = await fetchWithTimeout("https://get.geojs.io/v1/ip/geo.json")

			if (!response.ok) throw new Error(`The response from geojs was not OK. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from geojs")
			if (!data.ip || !data.country_code || !data.region) throw new Error("The JSON data is missing the ip, country_code, or region key from geojs")

			return {
				ip         : data.ip,
				countryCode: data.country_code,
				region     : data.region,
				service    : "geojs",
			}
		},

		ipinfo: async () => {

			const response = await fetchWithTimeout("https://ipinfo.io/json")

			if (!response.ok) throw new Error(`The response from ipinfo was not OK from ipinfo. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from ipinfo")
			if (!data.ip || !data.country || !data.region) throw new Error("The JSON data is missing the ip, country, or region key from ipinfo")

			return {
				ip         : data.ip,
				countryCode: data.country,
				region     : data.region,
				service    : "ipinfo",
			}
		},

		ipapi: async () => {

			const response = await fetchWithTimeout("https://ipapi.co/json")

			if (!response.ok) throw new Error(`The response from ipapi was not OK. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from ipapi")
			if (!data.ip || !data.country || !data.region) throw new Error("The JSON data is missing the ip, country, or region key from ipapi")

			return {
				ip         : data.ip,
				countryCode: data.country,
				region     : data.region,
				service    : "ipapi",
			}
		},
	}

	/**
	 * /**
	 * Cloudflare doesn't return the region.
	 * If the website manager has set a region such as California in the restricted_regions array,
	 * we have to validate the region. This is not possible with the data from Cloudflare.
	 * So we have to abort the process and give the other services a chance to return the region.
	 *
	 * If the country code is US,
	 * and the restricted_regions array contains "US-CA" or "US-VA",
	 * we must process the region.
	 *
	 * @param countryCode
	 * @returns {boolean}
	 */
	const processRegion = (countryCode) => {

		if (countryCode !== "US ") return false

		const regionsToCheck = ["US-CA", "US-VA"]

		return regionsToCheck.some(region =>
			pmwDataLayer?.general?.consent_management.restricted_regions.includes(region),
		)
	}

	/**
	 * Check if the visitor is allowed to trigger tracking events.
	 * This checks both bot user-agent patterns and custom IP exclusion list.
	 *
	 * When the IP exclusion list is populated, this blocks:
	 * - Browser-initiated S2S events
	 * - Browser pixel firing (via pmw._ipExcluded flag)
	 * Server-side purchase events are handled separately in PHP.
	 *
	 * @returns {Promise<boolean>} True if the visitor is allowed
	 */
	pmw.isVisitorAllowed = async () => {

		// Check the cache if visitor is allowed
		if (pmw.retrieveData("visitorAllowed") !== null && pmw.retrieveData("visitorAllowed") !== undefined) {
			const allowed = !!JSON.parse(pmw.retrieveData("visitorAllowed"))
			pmw._ipExcluded = !allowed
			return allowed
		}

		// Check if user agent is a bot
		if (pmw.isBotUserAgent()) {
			pmw.storeData("visitorAllowed", false)
			pmw._ipExcluded = true
			return false
		}

		// Check if IP is on the exclusion list (if custom exclusions are defined)
		const ipExcludeList = pmwDataLayer?.general?.server_2_server?.ip_exclude_list
		if (Array.isArray(ipExcludeList) && ipExcludeList.length > 0) {
			
			const ip = await pmw.getIp()
			
			if (isOnIpExclusionList(ip)) {
				pmw.storeData("visitorAllowed", false)
				pmw._ipExcluded = true
				pmw.console.log(`Visitor IP ${ip} is on the exclusion list — all tracking suppressed`)
				return false
			}
		}

		// If not a bot and not on IP exclusion list, visitor is allowed
		pmw.storeData("visitorAllowed", true)
		pmw._ipExcluded = false
		return true
	}

	/**
	 * Validates an IP address (IPv4 or IPv6)
	 *
	 * @param {string} ip - The IP address to validate
	 * @returns {boolean} True if the IP is valid
	 */
	pmw.isValidIpAddress = (ip) => {
		if (!ip || typeof ip !== "string") return false

		// Use the range_check library's isIP function for accurate validation
		if (!isIP(ip)) return false

		// Additional validation: ensure it's not a private/reserved IP
		// This is important for CAPI which needs public IPs
		return !pmw.isPrivateOrReservedIp(ip)
	}

	/**
	 * Check if an IP is private or reserved (not suitable for CAPI)
	 *
	 * @param {string} ip - The IP address to check
	 * @returns {boolean} True if the IP is private or reserved
	 */
	pmw.isPrivateOrReservedIp = (ip) => {
		if (!ip) return true

		// IPv4 private ranges
		const ipv4PrivateRanges = [
			"10.0.0.0/8",
			"172.16.0.0/12",
			"192.168.0.0/16",
			"127.0.0.0/8",
			"169.254.0.0/16", // Link-local
			"0.0.0.0/8",
		]

		// IPv6 private/reserved ranges
		const ipv6PrivateRanges = [
			"::1/128",         // Loopback
			"fc00::/7",        // Unique local
			"fe80::/10",       // Link-local
			"::/128",          // Unspecified
		]

		const privateRanges = isV4(ip) ? ipv4PrivateRanges : ipv6PrivateRanges

		// Use is-in-subnet for accurate range checking
		try {
			const isInSubnet = require("is-in-subnet")
			for (const range of privateRanges) {
				if (isInSubnet.isInSubnet(ip, range)) {
					return true
				}
			}
		} catch (e) {
			// If is-in-subnet check fails, fall back to basic checks
			// These cover the most common private ranges
			if (isV4(ip)) {
				if (ip.startsWith("10.") || 
					ip.startsWith("192.168.") || 
					ip.startsWith("127.") ||
					ip.startsWith("169.254.") ||
					ip.startsWith("0.")) {
					return true
				}
				// Check 172.16.0.0/12 range (172.16.x.x - 172.31.x.x)
				const parts = ip.split(".")
				if (parts[0] === "172") {
					const second = parseInt(parts[1], 10)
					if (second >= 16 && second <= 31) {
						return true
					}
				}
			} else {
				// IPv6 basic checks
				if (ip === "::1" || ip.startsWith("fc") || ip.startsWith("fd") || ip.startsWith("fe80:")) {
					return true
				}
			}
		}

		return false
	}

	/**
	 * Get the client IP address, with race condition prevention.
	 *
	 * Uses dual-stack ("any") endpoints only. Browser happy eyeballs (RFC 8305)
	 * returns IPv6 when the client actively uses it for external connections.
	 * This matches what ad platform browser pixels see, ensuring correct CAPI
	 * event matching. All configured services are raced concurrently.
	 *
	 * @returns {Promise<string|null>} The client IP address or null
	 */
	pmw.getClientIp = async () => {
		// If we already have a resolved IP, return it immediately
		const cachedIp = pmw.retrieveData("client_ip")
		if (cachedIp && pmw.isValidIpAddress(cachedIp)) {
			return cachedIp
		}

		// If detection is already in progress, wait for it
		if (ipDetectionPromise && !ipDetectionResolved) {
			return ipDetectionPromise
		}

		// Start a new detection process
		ipDetectionResolved = false
		ipDetectionPromise = (async () => {
			try {
				const ip = await pmw.getIp("any")

				if (ip && pmw.isValidIpAddress(ip)) {
					pmw.storeData("client_ip", ip)

					if (isV6(ip)) {
						pmw.console.log(`Using IPv6 address: ${ip}`)
					} else {
						pmw.console.log(`Using IPv4 address: ${ip}`)
					}

					ipDetectionResolved = true
					return ip
				}

				ipDetectionResolved = true
				return null
			} catch (error) {
				pmw.console.error("Error detecting client IP:", error)
				ipDetectionResolved = true
				return null
			}
		})()

		return ipDetectionPromise
	}

	/**
	 * Get IP with a custom timeout.
	 * Wraps getIp with a timeout that rejects if the detection takes too long.
	 *
	 * @param {string} ipVersion - The IP version to detect ("4", "6", or "any")
	 * @param {number} timeoutMs - Maximum time to wait in milliseconds
	 * @returns {Promise<string|null>} The detected IP or null
	 */
	pmw.getIpWithTimeout = async (ipVersion, timeoutMs) => {
		return Promise.race([
			pmw.getIp(ipVersion),
			new Promise((_, reject) => 
				setTimeout(() => reject(new Error(`IPv${ipVersion} detection timed out after ${timeoutMs}ms`)), timeoutMs)
			)
		]).catch(error => {
			pmw.console.log(error.message)
			return null
		})
	}

	pmw.getIp = async (ipVersion = "any", maxServicesToCheck = 0) => {

		const validVersions = ["any", "4", "6"]

		// return null if the version is not valid and console log an error
		if (!validVersions.includes(ipVersion)) {
			console.error("The version argument must be one of the following: " + validVersions.join(", "))
			return null
		}

		if (ipVersion === "any" && pmw.retrieveData("ip")) {
			return pmw.retrieveData("ip")
		}

		// Get the ipv data from the cache
		const ipv = pmw.retrieveData(`ipv${ipVersion}`)

		// Return it if it is not undefined
		// The value can be an IP
		// or null if we ran the test but no IP could be retrieved
		// It is undefined if the ipVersion was not tested yet
		if (ipv !== undefined) return ipv

		pmw.console.log("Detecting the browser IP address because one or more server-to-server APIs are enabled and require the browser IP.")

		try {// Check the cache if the IP is already stored

			const ip = await pmw.getIpFromAnyService(ipVersion, maxServicesToCheck)

			// Validate the IP before storing
			if (ip && !isIP(ip)) {
				pmw.console.warn(`Invalid IP address received: ${ip}`)
				return null
			}

			// Store the IP in the cache
			if (ipVersion === "any" && ip) {
				pmw.storeData("ip", ip)

				// Cross-cache: when a dual-stack endpoint returns IPv6,
				// also populate the ipv6 cache key so downstream consumers
				// (e.g., getFbUserDataFromBrowser) can find it
				if (isV6(ip)) {
					pmw.storeData("ipv6", ip)
				}
			}

			if (
				getIpVersion(ip) === 4
				|| (ipVersion === "4" && ip === null)
			) {
				pmw.storeData("ipv4", ip)
			}

			if (getIpVersion(ip) === 6
				|| (ipVersion === "6" && ip === null)
			) {
				pmw.storeData("ipv6", ip)
			}

			return ip
		} catch (error) {
			// console.error(error)
			return null
		}
	}

	pmw.getBrowserGeoFromExternalService = async () => {

		const startTime = performance.now()

		let promises = []

		Object.keys(pmw.getGeoInfo).forEach(service => {
			promises.push(pmw.getGeoInfo[service]())
		})

		try {
			const data = await Promise.any(promises)

			data.countryCode = data.countryCode.toUpperCase()

			if (data.region && data.region === "California") {
				data.regionCode = "US-CA"
			}

			if (data.region && data.region === "Virginia") {
				data.regionCode = "US-VA"
			}

			pmw.console.log(
				"Retrieved the browser geo info from an external service: ",
				data,
				" which took ",
				(performance.now() - startTime).toFixed(2) + " ms",
			)

			// pmw.storeData("countryCode", data.countryCode)
			// pmw.storeData("ip", data.ip)
			return data
		} catch (error) {
			pmw.console.error("error: ", error)
			return null
		}
	}

	const getBrowserLocationByTimeZone = () => {

		const geo         = require("./geo/geo.mjs")
		const geoLocation = geo.getLocationByTimeZone()

		if (geoLocation.countryCode) {
			return geoLocation
		}

		return null
	}

	pmw.getBrowserGeo = async () => {

		let geoFromStorage = {}

		if (pmw.retrieveData("geoCountryCode")) geoFromStorage.countryCode = pmw.retrieveData("geoCountryCode")
		if (pmw.retrieveData("geoRegionCode")) geoFromStorage.regionCode = pmw.retrieveData("geoRegionCode")
		if (pmw.retrieveData("geoRegion")) geoFromStorage.region = pmw.retrieveData("geoRegion")

		if (geoFromStorage.countryCode) {
			pmw.console.log("Retrieved the browser geo location from the cache: ", geoFromStorage)
			return geoFromStorage
		}

		let geoInfo = await pmw.getBrowserGeoFromExternalService()

		// If the external services fail, try to use the timezone fallback to get the country
		if (!geoInfo) {

			const gepLocationByTimeZone = getBrowserLocationByTimeZone()

			geoInfo = {}

			if (gepLocationByTimeZone) pmw.console.log("Retrieved the browser geo location from the timezone: ", gepLocationByTimeZone)
			if (gepLocationByTimeZone.countryCode) geoInfo.countryCode = gepLocationByTimeZone.countryCode
			if (gepLocationByTimeZone.regionCode) geoInfo.regionCode = gepLocationByTimeZone.regionCode
		}

		if (geoInfo?.ip) pmw.storeData("ip", geoInfo.ip)
		if (geoInfo?.countryCode) pmw.storeData("geoCountryCode", geoInfo.countryCode)
		if (geoInfo?.regionCode) pmw.storeData("geoRegionCode", geoInfo.regionCode)
		if (geoInfo?.region) pmw.storeData("geoRegion", geoInfo.region)

		if (geoInfo?.countryCode) {
			return geoInfo
		}

		return null
	}

	/**
	 * Get the IP from the visitor using different services.
	 * Return the first valid IP address that can be retrieved the fastest.
	 *
	 * @returns {Promise<IP>}
	 */
	pmw.getIpFromAnyService = async (ipVersion = "any", maxServicesToCheck = 0) => {

		// start tracking time
		const startTime = performance.now()

		// Filter services that support the requested ipVersion.
		// When maxServicesToCheck is 0 (default), race all available services
		// concurrently for maximum speed and reliability.

		let filteredKeys = Object.keys(ipServices)
			.filter(service => ipServices[service][ipVersion])

		if (maxServicesToCheck > 0) {
			filteredKeys = filteredKeys.slice(0, maxServicesToCheck)
		}

		const ipServicesFiltered = filteredKeys
			.reduce((obj, key) => {
					obj[key] = ipServices[key]
					return obj
				}
				, {})

		let promises = []

		Object.keys(ipServicesFiltered).forEach(service => {
			// only push if the ipVersion is available for the service
			if (ipServicesFiltered[service][ipVersion]) {
				// console.log(`pushing ${service} to promises array with ipVersion ${ipVersion}`)
				promises.push(pmw.getIpFromService(service, ipVersion))
			}
		})

		// If the promises array is not empty, return the first promise that resolves the fastest
		// Ignore the promises that reject
		if (promises.length > 0) {

			try {
				const data = await Promise.any(promises)
				// console.log(`The Pixel Manager retrieved the IP address ${data.ip} from ${data.service} using IP version ${data.ipVersion}`)

				// stop tracking time
				const endTime   = performance.now()
				const timeTaken = (endTime - startTime).toFixed(2)

				pmw.console.log(`Retrieved the IP address ${data.ip} from ${data.service} which took ${timeTaken} ms`)
				return data.ip
			} catch (error) {
				// console.error(error)
				pmw.console.log(`Could not retrieve an IP with version ${ipVersion} from any of the services.`)
				return null
			}

		} else {
			pmw.console.log("No promises to resolve")
			return null
		}
	}

	/**
	 * Fetches the IP address from a specified service.
	 *
	 * @async
	 * @param {string} service - The name of the service to fetch the IP from.
	 * @param {string} [ipVersion="any"] - The version of the IP (e.g., "any" for global).
	 * @returns {Promise<Object>} A promise that resolves to an object containing the IP address, service name, and IP version.
	 * @throws {Error} Will reject the promise with an error if the IP version is not available for the service, the response from the service is not OK, the returned value from the service is not an IP address, or any other error occurs.
	 */
	pmw.getIpFromService = async (service, ipVersion = "any") => {
		try {

			let data = {
				ip       : null,
				service  : service,
				ipVersion: ipVersion,
			}

			// Return reject if the ipVersion is not available for the service
			if (!ipServices[service][ipVersion]) return Promise.reject(new Error(`The IP version ${ipVersion} is not available for the service ${service}`))

			pmw.console.log(`Fetching IP from ${service} using IP version ${ipVersion}`)

			const response = await fetchWithTimeout(ipServices[service][ipVersion])

			if (!response.ok) {
				return Promise.reject(new Error(`The response from ${service} was not OK. Status: ${response.status}`))
			}

			let serviceResponseData = await response.text()

			if (service === "cloudflare") {
				// Convert key-value pairs to JSON
				serviceResponseData = serviceResponseData
					.trim()
					.split("\n")
					.reduce((obj, pair) => {
						pair = pair.split("=")
						return (obj[pair[0]] = pair[1]), obj
					}, {})

				data.ip = serviceResponseData.ip
			} else {
				data.ip = serviceResponseData.trim()
			}

			if (!isIP(data.ip)) {
				return Promise.reject(new Error(`The returned value from ${service} is not an IP address`))
			}

			return data
		} catch (error) {
			return Promise.reject(error)
		}
	}

	/**
	 * Cloudflare
	 *
	 * Response time: approx. 70ms
	 * IPv: Returns IPv4 or IPv6 depending on the client
	 * Dual stack: Doesn't offer dual stack detection
	 *
	 * @returns {Promise<*>}
	 */
	const getIpFromCloudflare = async () => {

		const response = await fetchWithTimeout("https://www.cloudflare.com/cdn-cgi/trace")
		let data       = await response.text()

		// Convert key-value pairs to JSON
		// https://stackoverflow.com/a/39284735/452587
		data = data
			.trim()
			.split("\n")
			.reduce((obj, pair) => {
				pair = pair.split("=")
				return obj[pair[0]] = pair[1], obj
			}, {})

		// return the data.ip not a promise
		return data.ip
	}

	/**
	 * Check if the visitor is NOT allowed to trigger server-to-server events
	 * This checks both bot user-agent patterns and custom IP exclusion list
	 *
	 * @returns {Promise<boolean>} True if the visitor should be blocked
	 */
	pmw.isVisitorNotAllowed = async () => {
		return await pmw.isVisitorAllowed() === false
	}

	/**
	 * Check if the current user agent matches known bot patterns
	 *
	 * @returns {boolean} True if the user agent is a bot
	 */
	pmw.isBotUserAgent = () => {
		const userAgent = navigator.userAgent || ''
		
		// If no user agent, assume it might be a bot
		if (!userAgent) {
			return true
		}

		const botPatterns = getBotPatterns()

		// Check if user agent matches any bot pattern (case-insensitive)
		for (const pattern of botPatterns) {
			if (userAgent.toLowerCase().includes(pattern.toLowerCase())) {
				return true
			}
		}

		return false
	}

	/**
	 * Get list of known bot user-agent patterns
	 *
	 * @returns {string[]} Array of bot patterns
	 */
	const getBotPatterns = () => {
		// Get custom patterns from pmwDataLayer if available
		let customPatterns = []
		if (Array.isArray(pmwDataLayer?.general?.server_2_server?.user_agent_exclude_patterns)) {
			customPatterns = pmwDataLayer.general.server_2_server.user_agent_exclude_patterns
		}

		// Default bot patterns - focusing on legitimate crawlers that identify themselves
		const defaultPatterns = [
			// Search engine crawlers
			'Googlebot',
			'Google-InspectionTool',
			'Google-Extended',
			'Storebot-Google',
			'bingbot',
			'BingPreview',
			'msnbot',
			'Slurp',
			'DuckDuckBot',
			'Baiduspider',
			'YandexBot',
			'Sogou',
			
			// Social media crawlers
			'facebookexternalhit',
			'FacebookBot',
			'LinkedInBot',
			'Twitterbot',
			'PinterestBot',
			'TikTokBot',
			'SnapchatAds',
			'DiscordBot',
			'InstagramBot',
			'WhatsApp',
			
			// SEO & Analytics
			'AhrefsBot',
			'SemrushBot',
			'MJ12bot',
			'DotBot',
			'rogerbot',
			
			// Monitoring services
			'UptimeRobot',
			'Pingdom',
			'StatusCake',
			'GTmetrix',
			
			// AI & Research
			'ClaudeBot',
			'GPTBot',
			'ChatGPT',
			'anthropic-ai',
			'CCBot',
			'PerplexityBot',
			
			// Generic patterns
			'crawler',
			'spider',
			'scraper',
			'bot/',
			'/bot',
			'bot-',
		]

		return defaultPatterns.concat(customPatterns)
	}

	/**
	 * Check if an IP address is in CIDR notation
	 *
	 * @param {string} ipName - The IP or CIDR notation to check
	 * @returns {boolean} True if the string contains CIDR notation
	 */
	const isCidr = (ipName) => {
		return ipName.includes('/')
	}

	/**
	 * Check if an IP address is on the custom exclusion list
	 *
	 * @param {string} ip - The IP address to check
	 * @returns {boolean} True if the IP is on the exclusion list
	 */
	const isOnIpExclusionList = (ip) => {
		const ipExcludeList = pmwDataLayer?.general?.server_2_server?.ip_exclude_list || []

		for (const ipName of ipExcludeList) {
			if (isCidr(ipName)) {
				// Check CIDR range
				if (isInSubnet.isInSubnet(ip, ipName)) {
					return true
				}
			} else if (ip === ipName) {
				// Exact IP match
				return true
			}
		}

		return false
	}

}(window.pmw = window.pmw || {}, jQuery))
